import {
  DriverPreference,
  ShipmentAccessorial,
  ShipmentDimUnit,
  ShipmentFreightClass,
  ShipmentPackagingType,
  ShipmentPalletType,
  ShipmentWeightUnit,
} from "app/pages/shipments/types/domain";
import { CargoGroup } from "features/cargo-groups/api/types";
import { ContainerSizeType, ContainerType } from "features/cargo-groups/types";
import { DateIsoString } from "types/DateIsoString";
import { Incoterms } from "types/Incoterms";
import { Mode, ModeQuotes } from "types/Mode";
import { QuoteRequestStatus } from "types/QuoteRequestStatus";
import { QuoteStatus } from "types/QuoteStatus";
import { RoutingTypeEnum } from "types/RoutingType";
import { TrailerSize } from "types/TrailerSize";
import { TrailerType } from "types/TrailerType";
import { UnloadingDetails } from "types/UnloadingDetails";

import { ShipperAddress } from "../address/types";
import { Shipper } from "../shippers/types";

export enum QuoteType {
  Additional = "ADDITIONAL",
  Both = "BOTH",
  Cheapest = "CHEAPEST",
  Fastest = "FASTEST",
  ForwarderPreference = "FORWARDER_PREFERENCE",
}

export enum ServiceLevel {
  Additional = "ADDITIONAL",
  Deferred = "DEFERRED",
  Express = "EXPRESS",
  ForwarderPreference = "FORWARDER_PREFERENCE",
  NonStop = "NON_STOP",
  Standard = "STANDARD",
}

export enum TempUnit {
  C = "C",
  F = "F",
}
export enum VolumeFormat {
  Dimensions = "DIMENSIONS",
  Volume = "VOLUME",
}

export interface QuoteRequestStop {
  id: number;
  address: ShipperAddress | null;
  start: DateIsoString | null;
  end: DateIsoString | null;
  is_time_tbd: boolean | null;
  note: string | null;
  /** Only for FCL/AIR. Required and only exists for first stop */
  cargo_ready_date?: DateIsoString | null;
  /** Only for FCL/AIR. Optional and only exists for last stop */
  target_delivery_date?: DateIsoString | null;
  distance_from_prior_stop?: number | null;
  booking_notes?: string | null;
  reference_number?: string | null;
}

export interface QuoteRequestFile {
  id: string;
  name: string;
  extension: string;
  url: string | null;
}

export type Truck = {
  id: number;
  trailer_size?: TrailerSize;
  trailer_type?: TrailerType;
  reference_number?: string;
  created_at?: string;
  updated_at?: string;
};

export type PackageGroup = {
  id?: number;
  packaging_type?: ShipmentPackagingType;
  packing_count?: number;
  pallet_count?: number;
  freight_class?: ShipmentFreightClass;
  nmfc_code?: string;
  is_stackable?: boolean;
  item_quantity: number;
  weight_per_package?: number;
  length_per_package?: number;
  height_per_package?: number;
  width_per_package?: number;
  dim_unit: ShipmentDimUnit;
  commodities?: string;
  is_hazardous?: boolean;
  hazardous_goods_details?: string;
  created_at?: string;
  updated_at?: string;
  volume_format?: VolumeFormat;
  volume_per_item?: number;
};

export type AirPackageGroup = {
  id?: number;
  packaging_type?: ShipmentPackagingType;
  item_quantity?: number;
  weight_per_package?: number;
  length_per_package?: number;
  height_per_package?: number;
  width_per_package?: number;
  volume_format?: VolumeFormat;
  volume_per_item?: number;
  commodities?: string;
  is_hazardous?: boolean;
  hazardous_goods_details?: string;
};

export type Container = {
  id?: number;
  size?: ContainerSizeType;
  type?: ContainerType;
  max_temp?: number;
  min_temp?: number;
  temp?: number;
  temp_unit?: TempUnit;
  is_hazardous?: boolean;
  requires_genset?: boolean;
  volume?: number;
  weight?: number;
  created_at?: string;
  updated_at?: string;
};

export type LoadSpec = {
  id?: number;
  trailer_size?: TrailerSize;
  trailer_type?: TrailerType;
  commodities?: string;
  is_palletized?: boolean;
  weight_unit?: ShipmentWeightUnit;
  max_temp?: number;
  min_temp?: number;
  temp?: number;
  temp_unit?: TempUnit;
  packaging_type?: ShipmentPackagingType;
  packing_count?: number;
  pallet_type?: ShipmentPalletType;
  pallet_count?: number;
  unit_count?: number;
  total_weight?: number;
  weight_per_unit?: number;
  driver_preference?: DriverPreference;
  created_at?: string;
  updated_at?: string;
  truck_quantity?: number;
  trucks?: Truck[];
  truck?: Truck;
  package_groups?: Array<PackageGroup>;
  accessorials?: Array<ShipmentAccessorial>;

  incoterms?: Incoterms;
  routing_type?: RoutingTypeEnum;
  include_customs_clearance?: boolean;
  service_levels?: Array<ServiceLevel>;
  volume_format?: VolumeFormat;
  total_volume?: number;
  item_quantity?: number;
  is_overweight?: boolean;
  cargo_ready_date?: string;
  target_delivery_date?: string;
  air_package_groups?: Array<AirPackageGroup>;

  containers?: Array<Container>;
  quote_types?: Array<QuoteType>;
  capacity_guarantee_required?: boolean;
  direct_sailing_required?: boolean;
};
export interface QuoteRequest {
  id: number;
  guid: string | null;
  mode: ModeQuotes;
  status: QuoteRequestStatus;
  template_deleted?: boolean;
  portex_id: string;
  reference_number: string | null;
  internal_reference_number: string | null;
  hide_internal_ref_till_dispatch: boolean;

  deadline_respond_at: DateIsoString | null;
  cargo_groups: CargoGroup[];
  goods_value: number | null;
  hazardous_goods_details: string | null;
  include_customs_clearance: boolean | null;
  incoterms: Incoterms | null;
  insurance_required: boolean | null;
  is_hazardous: boolean | null;
  is_overweight: boolean | null;
  note: string | null;
  routing_type: RoutingTypeEnum | null;
  shipper_id: Shipper["id"];
  shipper_name: string;
  booking_terms_and_conditions?: string;
  stops: QuoteRequestStop[];
  unloading_details: UnloadingDetails | null;
  equipment: string[];
  template_name: string | null;
  fileIds: string[];
  load_spec?: LoadSpec;
  carrier_routing_pref_notes?: string;
}

export type QuoteUnionByMode = {
  status: Extract<QuoteStatus, "SUBMITTED" | "BOOKED" | "CANCELED">;
  submitter_email: string;
  company_name: string | null;
  notes: string | null;
  file_ids: string[];
  booking_notes: string | null;
  created_at: DateIsoString | null;
  updated_at: DateIsoString | null;
  _quote_id: number;
  conversation_id?: number | null;
} & (
  | {
      mode: Extract<Mode, "FTL">;
      ftl_quote_id: number;
      rate_per_load: number;
      total_amount: number;
      valid_until?: string;
      min_transit_time: undefined;
      max_transit_time: undefined;
    }
  | {
      mode: Extract<Mode, "LTL">;
      ltl_quote_id: number;
      valid_until: DateIsoString | null;
      min_transit_time: number | null;
      max_transit_time: number | null;
      carrier_name: string;
      total_amount: number;
    }
  | {
      mode: Extract<Mode, "DRAYAGE">;
      drayage_quote_id: number;
      status: "BOOKED" | "CANCELED" | "SUBMITTED";
      submitter_email: string;
      total_amount: number;
      min_transit_time: number;
      max_transit_time: number;
      valid_until?: string;
      company_name: string;
      notes: string;
      booking_notes: string;
      created_at: string;
      updated_at: string;
      line_items: Array<{
        amount: number;
        type: LineItemType;
        cargo_group?: CargoGroup;
      }>;
    }
);

type ShipperUser = {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
};

export interface ShipperQuoteRequest extends QuoteRequest {
  original_qr_id: number | null;
  owner: ShipperUser;
  quotes: QuoteUnionByMode[];
  historical_quotes: QuoteUnionByMode[];
  booked_quote: QuoteUnionByMode | null;
  partners: {
    is_internal: false;
    has_responded: boolean;
    contact_id: number;
    company_name: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    quotes: QuoteUnionByMode[];
  }[];
  teammates: {
    is_internal: true;
    contact_id: number;
    email: string;
    first_name: string | null;
    last_name: string | null;
  }[];
  email_metadata: {
    from: string;
    to: string;
    cc: string[];
    bcc: string[];
    subject: string;
  };
  requested_at: DateIsoString | null;
  booked_at: DateIsoString | null;
  booked_by: ShipperUser | null;
  canceled_at: DateIsoString | null;
  canceled_by: ShipperUser | null;
  canceled_reason: string | null;
  closed_at: DateIsoString | null;
  closed_by: ShipperUser | null;
  closed_reason: string | null;
  created_at: DateIsoString | null;
  updated_at: DateIsoString | null;
  shipment_id: number | null;
}

export type AllowedUpdateStatus = Extract<QuoteStatus, "SENT" | "CLOSED" | "BOOKED">;

export type ShipperUpdateQuoteRequestBody = {
  id?: number;
  status?: AllowedUpdateStatus;
  templateDeleted?: boolean;
  referenceNumber?: string | null;
  internalReferenceNumber?: string | null;
  hideInternalRefTillDispatch?: boolean;

  deadline?: DateIsoString | null;
  hazardousGoodsDetails?: string | null;
  includeCustomsClearance?: boolean | null;
  incoterms?: Incoterms | null;
  insuranceRequired?: boolean | null;
  isHazardous?: boolean | null;
  isOverweight?: boolean | null;
  additionalNotes?: string | null;
  routingType?: RoutingTypeEnum | null;
  unloadingDetails?: UnloadingDetails | null;
  goodsValue?: number | null;
  stops?: Array<{
    start?: DateIsoString | null;
    end?: DateIsoString | null;
    isTimeTbd?: boolean;
    referenceNumber?: string;
    note?: string;
    address: {
      id?: ShipperAddress["id"];
      name?: string | null;
      address1?: string | null;
      address2?: string | null;
      city?: string | null;
      zip?: string | null;
      countryCode?: string | null;
      countryName?: string | null;
      provinceCode?: string | null;
      provinceName?: string | null;
      googlePlaceId?: string | null;
      googlePlaceDescription?: string | null;
      type?: ShipperAddress["type"];
    } | null;
  }>;
  partners?: Array<{ contactId: number }>;
  resendTo?: Array<{ contactId: number }>;
  teammates?: Array<{ contactId: number }>;
  reason?: string;
  sendNotification?: boolean;
  bookedQuoteId?: number;
  fileIds?: string[];
  bookingNotes?: string;
  proNumber?: string;
  bolNumber?: string;
  loadSpec?: LoadSpec;
  carrier_routing_pref_notes?: string;
};
export interface BrokerQuoteRequest extends QuoteRequest {
  files: QuoteRequestFile[];
}

export type LineItemType = "FREIGHT_CHARGE" | "ADDITIONAL_CHARGE";

export type BrokerQuoteResponse = {
  submitterEmail: string;
  submitterTimezone: string;
  totalAmount: number;
  notes: string;
} & {
  mode: Extract<Mode, "DRAYAGE">;
  validUntil: string;
  minTransitTime?: number;
  maxTransitTime?: number;
  lineItems?: Array<{
    amount: number;
    cargo_group_id?: number;
    type: LineItemType;
  }>;
};
